const MAX_DIMENSION = 1024;
const JPEG_QUALITY = 0.85;

export type ResizedImage = {
    /** Base64 JPEG without the `data:` prefix — ready for the search-party `imageData` field. */
    base64: string;
    /** The resized JPEG blob — the caller owns its lifecycle (e.g. `URL.createObjectURL` for a thumbnail). */
    blob: Blob;
};

/**
 * Scales an image down so its longest edge is at most MAX_DIMENSION and re-encodes it as JPEG.
 * The base64 payload rides through search-party to the image-search backend, so shrinking it
 * here keeps the request comfortably under the ingress body limit (and normalises HEIC on
 * browsers whose canvas can decode it). Browser-only — call from an event handler, never SSR.
 */
export const resizeImageToBase64 = (file: Blob): Promise<ResizedImage> =>
    new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(objectUrl);

            const longestEdge = Math.max(image.width, image.height);
            const scale = longestEdge > MAX_DIMENSION ? MAX_DIMENSION / longestEdge : 1;

            const canvas = document.createElement('canvas');
            canvas.width = Math.round(image.width * scale);
            canvas.height = Math.round(image.height * scale);

            const context = canvas.getContext('2d');
            if (!context) {
                reject(new Error('Could not get a canvas context'));
                return;
            }
            context.drawImage(image, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('Could not encode the image'));
                        return;
                    }
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        // result is a data URL ("data:image/jpeg;base64,XXXX") — strip the prefix.
                        const dataUrl = reader.result as string;
                        const base64 = dataUrl.split(',')[1];
                        if (!base64) {
                            // A malformed data URL would otherwise send an empty image to the BE
                            // and surface as a confusing 400; fail locally instead.
                            reject(new Error('Could not encode the image'));
                            return;
                        }
                        resolve({ base64, blob });
                    };
                    reader.onerror = () => reject(reader.error ?? new Error('Could not read the image'));
                    reader.readAsDataURL(blob);
                },
                'image/jpeg',
                JPEG_QUALITY
            );
        };

        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Could not load the image'));
        };

        image.src = objectUrl;
    });
